<template>
    <ion-page>
        <ion-header :translucent="true">
            <ion-toolbar>
                <ion-title>Mitarbeiter</ion-title>
            </ion-toolbar>
            <ion-toolbar>
                <IonSearchbar animated :placeholder="' Name'" @ionInput="onSearch"></IonSearchbar>
            </ion-toolbar>
            <IonProgressBar type="indeterminate" v-if="loadAll.loading.value" style=""/>
        </ion-header>
        <ion-content class="no-padding-top">

            <Skeleton v-if="employeeLoader.pending.value" :items="['title', 'line', 'chip','title', 'line', 'chip','title', 'line', 'chip']"></Skeleton>
            <ion-list v-else>
                <ion-item
                    v-for="employee in displayedEmployees"
                    @click="onClickEmployee(employee)"
                    :key="employee.id"
                >
                    <IonLabel>
                        <h3 class="employee-name ">
                            <span
                                color="secondary"
                                fill="clear"
                                class="profile-button">
                                <img v-if="employee.profilePicturePath" :src="employee.profilePicturePath">
                                <ion-icon v-else slot="icon-only" src="/icons/user.svg" />
                                <IonBadge v-if="employeesNeedAction.get(employee.id) ?? 0 > 0" color="danger">
                                    {{ employeesNeedAction.get(employee.id) }}
                                </IonBadge>
                            </span>

                            {{ employee.name }}
                        </h3>
                        <p class="employee-subtitle">
                            <span v-if="employeeIsImpersonated(employee)">
                                aktuell ausgewählt
                            </span>
                            <span v-if="employee.employeeQualification">
                                {{ formatEmployeeQualification(employee.employeeQualification) }}
                            </span>
                            <template v-if="relevantContracts.has(employee.id) && !employeeContractValidToday(relevantContracts.get(employee.id) as EmployeeContract)">
                                <span>
                                    Vertragsanfang {{date.formatMonthFromKapitelDate((relevantContracts.get(employee.id) as EmployeeContract).validFrom)}} ({{ relevantContracts.get(employee.id)?.monthlyHours }}h)
                                </span>
                            </template>
                            <template v-else-if="relevantContracts.has(employee.id)">
                                <span>{{ relevantContracts.get(employee.id)?.monthlyHours }}h/Monat</span>
                                <AZKValue v-if="employee.plannedAbsences" :planned-absences="employee.plannedAbsences" :compact="true" :chipped="false"/>
                                <VacationValue v-if="employee.plannedAbsences" :planned-absences="employee.plannedAbsences" :compact="true" :chipped="false"/>
                            </template>
                        </p>
                        <Skeleton v-if="autopilotLoader.pending.value" :items="['chip']"/>
                        <span
                            v-else
                            v-for="calendarMonth in employeeMonths(employee)"
                            class="autopilotStatusMonthSummaryChips"
                        >

                            <AutopilotStatusMonthSummaryChip
                                v-if="!autopilotLoader.pending.value"
                                :planning-status="getPlanningStatusFor(employee, calendarMonth)"
                                :calendar-month="calendarMonth"
                                :month-label-format="'MMM'"
                                class="employeeSelectorStatusChip"
                            />
                        </span>
                    </IonLabel>
                </ion-item>
            </ion-list>

            <ion-modal
                :is-open="isEmployeeModalOpen"
                @didDismiss="isEmployeeModalOpen = false; selectedEmployee = undefined"
                :initial-breakpoint="0.75"
                :breakpoints="[0, 0.75, 1]"
            >
                <ion-header>
                    <ion-toolbar>
                        <ion-buttons slot="start">
                            <ion-button @click="isEmployeeModalOpen = false">Schließen</ion-button>
                        </ion-buttons>
                        <ion-title>{{ selectedEmployee?.name }}</ion-title>
                    </ion-toolbar>
                    <IonProgressBar type="indeterminate" v-if="autopilotLoaderOfSelectedEmployee.loading.value || autopilotLoader.loading.value" style=""/>
                </ion-header>
                <ion-content class="ion-padding" v-if="selectedEmployee">
                    <IonButton expand="block" color="primary" @click="onImpersonateEmployee">Als {{selectedEmployee.name}} anmelden</IonButton>
                    <IonAccordionGroup>
                        <IonAccordion>
                            <IonItem slot="header" color="light">
                                <h3>Lieblingsstationen</h3>
                            </IonItem>
                            <ion-list slot="content">
                                <ion-item v-for="jl in selectedEmployee.favoriteJobsites.map((j) => getShortJobsiteDescription(j, false, ' - ')).toSorted()"
                                >
                                    <ion-label>{{jl}}</ion-label>
                                </ion-item>
                            </ion-list>
                        </IonAccordion>
                    </IonAccordionGroup>


                    <template v-for="calendarMonth in employeeMonths(selectedEmployee)">
                        <h2>{{ date.formatKapitelDate(calendarMonth.begin, 'MMMM yyyy') }}</h2>

                        <template v-if="!getPlanningStatusFor(selectedEmployee, calendarMonth)?.bookingCompleted">
                            Voraussichtlich kommen noch <b>{{
                                floatFormat((getPlanningStatusForSelectedEmployee(calendarMonth)?.predictedAdditionalBookings ?? 0), 1)
                            }} Buchungen</b> hinzu.
                        </template>

                        <Skeleton v-if="autopilotLoader.loading.value" :items="['chip']" />
                        <AutopilotStatusMonthSummaryChip
                            v-else
                            :planning-status="getPlanningStatusFor(selectedEmployee, calendarMonth)"
                            :employee="selectedEmployee"
                            :calendar-month="calendarMonth"
                            class="employeeDetailsStatusChip"
                            :compact="false"
                            :monthLabelFormat="''"
                        />

                        <IonAccordionGroup>
                            <IonAccordion toggleIcon="">
                                <IonItem slot="header" style="padding-left: 0" color="light">
                                    <h3>
                                        Aktive Schichten:
                                        <IonChip class="small" v-for="shift in getPlanningStatusForSelectedEmployee(calendarMonth)?.shiftPortfolio?.toSorted(sortShifts) || []">
                                            {{shift}}
                                        </IonChip>
                                    </h3>
                                </IonItem>
                            </IonAccordion>
                        </IonAccordionGroup>

                        <IonAccordionGroup>
                            <IonAccordion>
                                <IonItem slot="header" style="padding-left: 0" color="light">
                                    <h3>Aktive Stationen</h3>
                                </IonItem>
                                <div slot="content">
                                    <ion-list>
                                        <ion-item v-for="aj in getJobsitesSortedByLabel(getPlanningStatusForSelectedEmployee(calendarMonth)?.autopilotStatus?.autopilotPreferences.autopilotJobsites.map((aj => aj.jobsite)) || [])"
                                        >
                                            <ion-label>{{getShortJobsiteDescription(aj,  false, " - ")}}</ion-label> (<strong>#
                                            {{
                                                floatFormat(getPlanningStatusForSelectedEmployee(calendarMonth)?.avgDemandPerMonth?.find(
                                                    avg => avg.jobsite.id === aj.id)?.currentPrediction || 0, 2)
                                            }}</strong>&nbsp;/ ⌀ {{
                                                floatFormat(getPlanningStatusForSelectedEmployee(calendarMonth)?.avgDemandPerMonth?.find(
                                                    avg => avg.jobsite.id === aj.id)?.averageMonthlyDemand || 0, 2)
                                            }})
                                        </ion-item>
                                    </ion-list>
                                    <ul style="font-size: 9pt; padding-left: 1em;">
                                        <li># aktuelle Vorhersage des noch offenen Bedarfs ({{
                                                (getPlanningStatusForSelectedEmployee(calendarMonth)?.shiftPortfolio?.toSorted(sortShifts) || []).join(", ")
                                            }})</li>
                                        <li>⌀ monatlicher Bedarf ({{
                                                (getPlanningStatusForSelectedEmployee(calendarMonth)?.shiftPortfolio?.toSorted(sortShifts) || []).join(", ")
                                            }})</li>
                                    </ul>
                                </div>
                            </IonAccordion>
                        </IonAccordionGroup>
                    </template> 
                        
                </ion-content>
            </ion-modal>
        </ion-content>
    </ion-page>
</template>

<script setup lang="ts">
import {
    IonAccordion,
    IonAccordionGroup,
    IonBadge,
    IonButton,
    IonButtons,
    IonChip,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonProgressBar,
    IonSearchbar,
    IonTitle,
    IonToolbar,
    loadingController,
    onIonViewWillEnter,
    SearchbarCustomEvent
} from '@ionic/vue'
import {
    employeeContractIsOrWillBeValid,
    employeeContractValidToday,
    fetchEmployees,
    formatEmployeeQualification
} from '@/helper/employee'
import {useAuthStore} from '@/store/auth'
import {computed, ComputedRef, Ref, ref} from 'vue'
import {useRouter} from 'vue-router'
import Skeleton from "@/components/Skeleton.vue";
import {DataLoader} from "@/helper/dataLoader.js";
import {
    AInesObjective,
    Employee,
    EmployeeContract, EmployeeSelectorEmployeeFragment,
    JobsiteFragmentFragment,
    PlanningStatusFragment,
    PlanningStatusPrecrimeFragment
} from "@/graphql/generated/graphql";
import {useAppState} from '@/helper/appState'
import {fetchCurrentPlanningStatus, fetchPlanningStatusWithPrecrime} from '@/helper/autopilotStatus'
import {CalendarMonth, generateCalendarMonth, generateCalendarMonths, sorter} from '@/helper/calendar/calendarMonth'
import date, {cmpKapitelDate} from '@/helper/datetime/date'
import AutopilotStatusMonthSummaryChip from "@/views/components/Planning/AutopilotStatusMonthSummaryChip.vue";
import useOfflineHelper from "@/helper/offline";
import {getShortJobsiteDescription} from "@/helper/jobsite";
import {fetchAInesObjectives} from "@/helper/chat/ainesObjectives";
import AZKValue from "@/views/components/Planning/AZKValue.vue";
import {floatFormat} from "@/helper/amountFormatter";
import {sortShifts} from "@/helper/shiftTypes";
import {computedAsync} from "@vueuse/core";
import {kapitelMonthString} from "@/graphql/kapitelTypes";
import VacationValue from "@/views/components/Planning/VacationValue.vue";
import month from "@/helper/datetime/month";

const authStore = useAuthStore()

const router = useRouter()

/** 
 * employees
 */

const employees : Ref<EmployeeSelectorEmployeeFragment[]> = ref([])
const relevantContracts : ComputedRef<Map<number,EmployeeContract>> = computed(() => {
    const m = new Map();
    employees.value.forEach((employee) => {
        const contract = employee.contracts.find(employeeContractIsOrWillBeValid)
        if (contract) {
            m.set(employee.id, contract);
        }
    })
    return m
})
const activeEmployees = computed(() => employees.value.filter((employee) => relevantContracts.value.has(employee.id)).sort((a,b)=>{
    let compare = cmpKapitelDate(a.firstContract.validFrom, b.firstContract.validFrom)
    if (compare === 0) {
        compare = a.id > b.id ? 1:-1
    }
    return compare
}))

const employeeLoader = new DataLoader(
    async () => {
        employees.value = await fetchEmployees()
    })

const employeeIsImpersonated = (employee: EmployeeSelectorEmployeeFragment) => {
    return employee.user.userName === authStore.getImpersonationUsername()
}

const searchString : Ref<string|undefined> = ref(undefined)
const onSearch = (value: SearchbarCustomEvent) => searchString.value = value.detail.value ?? undefined

const displayedEmployees = computed(() => activeEmployees.value.filter(e => searchString.value ? e.name?.toLowerCase().includes(searchString.value.toLowerCase()) : true))

/**
 *  autopilot Status
 */
const startMonth = month.sub(date.startOfMonth(date.getToday()), 1)

const planningStatus : Ref<PlanningStatusFragment[]> = ref([])
const autopilotLoader = new DataLoader(async () => {
    planningStatus.value = await fetchCurrentPlanningStatus(startMonth)
})
const getPlanningStatusFor = (employee: EmployeeSelectorEmployeeFragment, calendarMonth: CalendarMonth) => planningStatus.value.find(planningStatus => planningStatus.month === calendarMonth.getId() && planningStatus.employee.id === employee.id)

const employeeMonths = (employee: EmployeeSelectorEmployeeFragment) : CalendarMonth[] => {
    const employeeStatusMonths = planningStatus.value
        .filter(aps => aps.employee.id === employee.id)
        .map(aps => generateCalendarMonth(aps.month))
        .sort(sorter);

    let from = startMonth

    const contract = relevantContracts.value.get(employee.id)
    if (contract && !employeeContractValidToday(contract)) {
        from = contract.validFrom as kapitelMonthString
    }
    if (employeeStatusMonths[0] && employeeStatusMonths[0].begin < from) {
        from = employeeStatusMonths[0].begin as kapitelMonthString
    }

    let to = date.addMonths(from, 1)
    const lastStatusMonth = employeeStatusMonths[employeeStatusMonths.length-1]
    if (lastStatusMonth && lastStatusMonth.begin > to) {
        to = lastStatusMonth.begin
    }

    return generateCalendarMonths(from, undefined, to)
}


/**
 * objectives
 */
const employeesNeedAction: Ref<Map<Number, Number>> = ref(new Map())
const objectivesLoader = new DataLoader(async (signal: AbortSignal) => {
    for (const employee of activeEmployees.value) {
        if (!signal.aborted) {
            const needActionObjectives =  (await fetchAInesObjectives(employee.id)).filter(o => o.needsAction).length;
            employeesNeedAction.value.set(employee.id, needActionObjectives);
        }
    }
})


/**
 * employee details
 */

const selectedEmployee = ref<EmployeeSelectorEmployeeFragment|undefined>(undefined)
const isEmployeeModalOpen = ref(false)

const planningStatsWithPrecrimOfSelectedEmployee : Ref<(PlanningStatusFragment&PlanningStatusPrecrimeFragment)[] | undefined> = ref(undefined)
const autopilotLoaderOfSelectedEmployee = new DataLoader(async () => {
    if(!selectedEmployee.value){
        return []
    }
    const months = employeeMonths(selectedEmployee.value)

    planningStatsWithPrecrimOfSelectedEmployee.value = await Promise.all(months.map(month => fetchPlanningStatusWithPrecrime(month, selectedEmployee.value?.id))).then(arr => arr.filter(a => !!a))
})

const getPlanningStatusForSelectedEmployee = (calendarMonth: CalendarMonth) : (PlanningStatusFragment&PlanningStatusPrecrimeFragment) | undefined => planningStatsWithPrecrimOfSelectedEmployee.value?.find(planningStats => planningStats.month === calendarMonth.getId())

const getJobsitesSortedByLabel = (jobsites: JobsiteFragmentFragment[]) => {
    return jobsites.sort((a, b) => {
        const labelA = getShortJobsiteDescription(a, false, ' - ')
        const labelB = getShortJobsiteDescription(b, false, ' - ')
        return labelA.localeCompare(labelB)
    })
}

const onClickEmployee = (employee: EmployeeSelectorEmployeeFragment) => {
    if(useOfflineHelper().isOffline.value){
        return;
    }
    selectedEmployee.value = employee
    isEmployeeModalOpen.value = true
    autopilotLoaderOfSelectedEmployee.reload()
}

const onImpersonateEmployee = async () => {
    if(!selectedEmployee.value){
        return;
    }

    cancelAll()

    const loading = await loadingController.create({
        message: 'Mitarbeiterwechsel...'
    });
    loading.present();
    await useAppState().impersonate(selectedEmployee.value.user)
    loading.dismiss()

    isEmployeeModalOpen.value = false

    router.push('/')
}





/** global loader */

const loadAll = new DataLoader(async () => {
    const employeePromise = employeeLoader.load()
    const autopilotPromise = autopilotLoader.load()

    const objectiveLoader = employeePromise.then(async (employees) => { await objectivesLoader.load()})
    await Promise.all([employeePromise, autopilotPromise, objectiveLoader])
})

const cancelAll = () => {
    if (employeeLoader.loading.value) {employeeLoader.cancel()}
    if (autopilotLoader.loading.value) {autopilotLoader.cancel()}
    if (objectivesLoader.loading.value) {objectivesLoader.cancel()}
}

onIonViewWillEnter(loadAll.load)

</script>
<style lang="scss" scoped>
.employee-name {
    font-size: 1.2em;
}
.employee-subtitle {
    > * {
        margin-right: 1em;
    }
}
.profile-button {
    margin-right:5px;
    vertical-align: middle;
    --SIZE: 1.5em;
    width: var(--SIZE);
    height: var(--SIZE);
    img,ion-icon {
        width: var(--SIZE);
        height: var(--SIZE);
        border-radius: var(--SIZE);
        overflow: hidden;
        object-fit: cover;
    }

    position: relative; /*anchor*/

    ion-badge {
        position: absolute;
        left: calc(var(--SIZE) - 0px);
        top: -20px;
    }
}

.employeeSelectorStatusChip {
    margin-inline-start: 0;
    font-size: var(--custom-font-size-regular);
}
.employeeDetailsStatusChip {
    margin-inline-start: 0;
    font-size: var(--custom-font-size-regular);
}
</style>
